import type { Locals } from '@services/MarppleShop/shared/type/locals.ts';
import { KeyboardManager } from '@services/MarppleShop/shared/util/keyboardutil.ts';
import { type Html, html, on, View, CustomEventWithoutDetail } from 'rune-ts';
import { MShopUtilF } from '../../../../../../modules/MShop/Util/F/Function/module/MShopUtilF';
import { classes, htmlIf, htmlIfElse } from '../../../../shared/util';
import { getSharedData } from '../../../../shared/util/rune';
import { ModalCloseIcon } from '../Icon';
import klass from './Modal.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { ArrowLeftIcon } from '@services/MarppleShop/shared/components/atoms/Icon/foundation_icons.ts';

type ModalTitleAlign = 'left' | 'center';
type TitleOptions = {
  title_align?: ModalTitleAlign;
  use_close_btn?: boolean;
  use_left_btn?: boolean;
  use_left_blank_space?: boolean;
  custom_left_btn?: Html;
  left_btn_klass?: string;
  title_klass?: string;
};

export interface ModalProps {
  isOpen: boolean;
  backdrop?: boolean;
  backdrop_not_close?: boolean;
  is_bottom_sheet?: boolean;
  size: 'medium' | 'large' | 'free';
  title?: string | Html;
  title_options?: TitleOptions;
  body: Html | string | View;
  footer?: Html | View;
  disable_escape_keydown?: boolean;
  full_width?: boolean;
  enable_app_bottom_padding?: boolean;
  skip_body_fix?: boolean;
  full_body_height?: boolean;
  container_klass?: string;
  modal_klass?: string;
  body_klass?: string;
  // eslint-disable-next-line no-use-before-define
  onClose?: (view: Modal) => void | Promise<void>;
}

// modal 닫힐 때 호출
export class ModalClosingEvent<T = undefined> extends CustomEvent<T> {}

// modal close animation 끝나고 호출됨
export class ModalClosedEvent<T = undefined> extends CustomEvent<T> {}

export class ModalTitleLeftButtonClick extends CustomEventWithoutDetail {}

export class Modal extends View<ModalProps> {
  static readonly textAlignOptions = {
    left: {
      align_class: klass.left_align,
    },
    center: {
      align_class: klass.center_align,
    },
  };

  private originalOnClose = this.data.onClose;
  private _closed = true;
  private keyBoardManager: KeyboardManager;
  static FOOTER_PADDING = 16;

  constructor(props: ModalProps) {
    super(props);
    this.keyBoardManager = new KeyboardManager(this.handleKeyboardShow, this.handleKeyboardHide);
  }

  /**
   * @TODO: 헤더가 너무 복잡함 간소화 필요 (header 템플릿을 받는 방식으로) @blan22
   */
  protected defaultHeader(title: string | Html, title_options: TitleOptions) {
    const {
      title_align = 'left',
      use_close_btn = true,
      use_left_btn = false,
      use_left_blank_space = false,
      custom_left_btn,
      left_btn_klass = '',
      title_klass,
    } = title_options;

    return html`
      <div
        class="${classes(klass.header, shareFont('20_bold', '16_bold'), {
          [title_klass ?? '']: !!title_klass,
        })}"
      >
        <div class="${klass.left_area}">
          ${use_left_btn
            ? html` <button type="button" class="${klass.custom_btn} ${left_btn_klass}">
                ${custom_left_btn
                  ? html`${custom_left_btn}`
                  : ArrowLeftIcon({ klass: `${klass.arrow_left_icon}` })}
              </button>`
            : use_left_blank_space
              ? html`<div class="${klass.left_button_area}"></div>`
              : html``}
          ${htmlIfElse(
            typeof title === 'string',
            html`<p class="${klass.title} ${Modal.textAlignOptions[title_align].align_class}">${title}</p>`,
            title,
          )}
        </div>
        ${use_close_btn
          ? html` <div class="${klass.right_area}">
              <button type="button" class="${klass.close_btn}">${ModalCloseIcon()}</button>
            </div>`
          : html``}
      </div>
    `;
  }

  protected defaultFooter(footer: Html | View) {
    return html` <div
      class="${klass.footer}"
      ${this.tabBarPadding ? html`style="padding-bottom: ${this.tabBarPadding}px"` : ''}
    >
      ${footer}
    </div>`;
  }

  protected override template(data: ModalProps) {
    const { isOpen, backdrop = true, size, title, title_options = {}, body, footer, is_bottom_sheet } = data;

    return html`
      <div
        class="${classes(klass.modal_container, {
          [this.data.container_klass || '']: this.data.container_klass,
          [klass.bottom_sheet]: is_bottom_sheet,
          [klass.open]: isOpen,
        })}"
      >
        ${htmlIf(
          html`
            <div
              class="${classes(klass.backdrop, {
                [klass.open]: isOpen,
              })}"
            ></div>
          `,
          backdrop,
        )}
        <div
          class="${classes(klass.modal, {
            [this.data.modal_klass || '']: this.data.modal_klass,
            [klass.bottom_sheet]: is_bottom_sheet,
            [klass.medium]: size === 'medium',
            [klass.large]: size === 'large',
          })}"
        >
          ${title ? this.defaultHeader(title, title_options) : ''}
          <div
            class="${klass.body} ${classes({
              [this.data.body_klass || '']: this.data.body_klass,
              [klass.full_width]: this.data.full_width,
              [klass.full_body_height]: this.data.full_body_height,
            })} rune-modal-body"
          >
            ${body} ${this.tabBarPadding ? html` <div style="height: ${this.tabBarPadding}px"></div> ` : ''}
          </div>
          ${footer ? this.defaultFooter(footer) : ''}
        </div>
      </div>
    `;
  }

  protected override onUnmount() {
    this.keyBoardManager.onDestroy();
  }

  override onRender() {
    if (!this.data.disable_escape_keydown) {
      window.addEventListener('keydown', (e) => {
        if (e.code !== 'Escape') {
          return;
        }

        if (!this._closed) {
          this.close();
        }
      });
    }

    this.keyBoardManager.onMount();
  }

  async close<T = undefined>(param?: T) {
    if (this._closed) return;
    await this.originalOnClose?.(this);
    const element = this.element();
    if (!element) return;
    const modal = element.querySelector(`.${klass.modal}`);
    const backdrop = element.querySelector(`.${klass.backdrop}`);
    const onEndAnimation = () => {
      modal?.classList.remove(klass.animate, klass.slide_out);
      element.classList.remove(klass.open);
      modal?.removeEventListener('animationend', onEndAnimation);
      if (!this.data.skip_body_fix) {
        MShopUtilF.bodyFixed$(false);
      }
      document.body.classList.remove(klass.modal_fixed);

      this.dispatchEvent(ModalClosedEvent, {
        bubbles: true,
        detail: param,
      });
    };

    modal?.addEventListener('animationend', onEndAnimation);
    modal?.classList.add(klass.animate, klass.slide_out);
    backdrop?.classList.remove(klass.open);

    this.dispatchEvent(ModalClosingEvent, {
      bubbles: true,
      detail: param,
    });
    this._closed = true;
  }

  open() {
    const element = this.element();
    if (!element) return;

    const modal = element.querySelector(`.${klass.modal}`);
    const backdrop = element.querySelector(`.${klass.backdrop}`);
    // modal?.addEventListener('animationend', () => {
    //   modal?.classList.remove(klass.animate, klass.slide_in);
    // });
    element.classList.add(klass.open);
    backdrop?.classList.add(klass.open);
    modal?.classList.add(klass.animate, klass.slide_in);
    if (!this.data.skip_body_fix) {
      MShopUtilF.bodyFixed$(true);
    }
    document.body.classList.add(klass.modal_fixed);
    this._closed = false;
  }

  @on('click', `.${klass.backdrop}`)
  _clickBackDrop() {
    if (this.data.backdrop_not_close) return;
    this.close();
  }

  @on('click', `.${klass.close_btn}`)
  _closeModal() {
    this.close();
  }

  @on('click', `.${klass.custom_btn}`)
  _clickLeftBtn() {
    this.dispatchEvent(ModalTitleLeftButtonClick, { bubbles: true });
  }

  public scrollBodyTo(x: number, y: number) {
    const body$ = this.element().querySelector(`.${klass.body}`);
    body$?.scrollTo(x, y);
  }

  private handleKeyboardShow = () => {
    const footer$ = document.querySelector<HTMLElement>(`.${klass.footer}`);
    if (footer$) {
      footer$.style.paddingBottom = `${Modal.FOOTER_PADDING}px`;
    }
  };

  private handleKeyboardHide = () => {
    const footer$ = document.querySelector<HTMLElement>(`.${klass.footer}`);
    if (footer$) {
      footer$.style.paddingBottom = `${this.tabBarPadding}px`;
    }
  };

  private get tabBarPadding() {
    const webviewapp = getSharedData(this)?.webviewapp as Locals['webviewapp'];

    if (webviewapp?.tab_bar_height && this.data.enable_app_bottom_padding) {
      return webviewapp.tab_bar_height + Modal.FOOTER_PADDING;
    }

    return 0;
  }

  public setBody(body: Html | string | View) {
    this.data.body = body;
    return this;
  }

  public setTitle(title?: string | Html) {
    this.data.title = title;
    if (this.isRendered()) {
      this.redraw();
    }
    return this;
  }

  public redrawBody() {
    const $body = this.element().querySelector(`.${klass.body}`);

    if (!$body?.firstElementChild) return;

    if (this.data.body instanceof View) {
      $body.replaceChild(this.data.body.render(), $body.firstElementChild);
    } else if (typeof this.data.body === 'string') {
      $body.firstElementChild.outerHTML = this.data.body;
    }

    return this;
  }
}
